import React, { FC, useEffect } from 'react';
import {
    Route,
    Switch,
    useRouteMatch,
    Redirect,
    useLocation,
} from 'react-router-dom';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Spinner } from '@component/Spinner';
import { PortalDialog } from '@component/Portal';
import type { AppState } from '@app/react/reducer';
import { OrderHaneSelectContainer } from '@container/OrderHaneSelectContainer';
import { OrderHazuSelectContainer } from '@container/OrderHazuSelectContainer';
import { OrderHazumakiSelectContainer } from '@container/OrderHazumakiSelectContainer';
import { OrderHonsuSelectContainer } from '@container/OrderHonsuSelectContainer';
import { OrderInsertSelectContainer } from '@container/OrderInsertSelectContainer';
import { OrderKamonContainer } from '@container/OrderKamonContainer';
import { OrderKebikiSelectContainer } from '@container/OrderKebikiSelectContainer';
import { OrderMozikokuinContainer } from '@container/OrderMozikokuinContainer';
import { OrderLameSelectContainer } from '@container/OrderLameSelectContainer';
import { OrderShaftSelectContainer } from '@container/OrderShaftSelectContainer';
import { OrderWashiSelectContainer } from '@container/OrderWashiSelectContainer';
import { OrderYashakuSelectContainer } from '@container/OrderYashakuSelectContainer';
import { OrderYajiriSelectContainer } from '@container/OrderYajiriSelectContainer';
import { OrderCustomizeDesignContainer } from '@container/OrderCustomizeDesignContainer';
import { OrderTypeZeroSelectContainer } from '@container/OrderTypeZeroSelectContainer';
import { OrderDecorationSelectContainer } from '@container/OrderDecorationSelectContainer';
import { OrderFeatherinsideSelectContainer } from '@container/OrderFeatherinsideSelectContainer';
import { useMediaDesktop, useMediaMobile } from '@app/react/common/useMedia';
import { attentionModalFunc } from '@app/react/common/attentionModalFunc';
import { HistoryGoPartsContext } from '@app/react/contexts/useHistoryGoParts';
import { useHistoryGoParts } from '@app/react/common/Function';
import { OrderPathParamsContext } from '@app/react/contexts/useOrderPathParams';
import { useOrderPathParams } from '@app/react/common/useOrderPathParams';

import { SlideAnimation } from './SlideAnimation';
import { Preview } from './PreviewContainer';
import { DesktopRender, MobileRender } from './NaviContainer';

const initProductSetState = (state: AppState) =>
    state.uiCustomDesign.initProductSetState;

export const OrderPartsContainer: FC = () => {
    const goParts = useHistoryGoParts();

    const { path, url } = useRouteMatch();
    const location = useLocation();
    const orderPathParams = useOrderPathParams();
    const isDesktop = useMediaDesktop();
    const isMobile = useMediaMobile();
    const fetchciState = useSelector(initProductSetState);
    const loadingClassName = classNames('loading', {
        active: fetchciState === 'request',
    });

    useEffect(() => {
        window.onbeforeunload = attentionModalFunc;

        return () => {
            window.onbeforeunload = null;
        };
    }, []);

    return (
        <>
            <OrderPathParamsContext.Provider value={orderPathParams}>
                <HistoryGoPartsContext.Provider value={goParts}>
                    <section
                        id="parts-container-buy-view"
                        className="parts-container"
                    >
                        <div className="parts-container__shaft-3d">
                            <Switch location={location}>
                                <Route
                                    path={[`${path}/:partsName`, `${path}`]}
                                    render={(props) =>
                                        isDesktop ? (
                                            <DesktopRender {...props} />
                                        ) : (
                                            <MobileRender {...props} />
                                        )
                                    }
                                />
                            </Switch>
                        </div>

                        <SlideAnimation location={location}>
                            <Switch location={location}>
                                <Route exact path={`${path}`}>
                                    <OrderCustomizeDesignContainer />
                                </Route>

                                <Route exact path={`${path}/shaft`}>
                                    <OrderShaftSelectContainer />
                                </Route>

                                <Route exact path={`${path}/hane`}>
                                    <OrderHaneSelectContainer />
                                </Route>

                                <Route exact path={`${path}/washi`}>
                                    <OrderWashiSelectContainer />
                                </Route>

                                <Route exact path={`${path}/kebiki`}>
                                    <OrderKebikiSelectContainer />
                                </Route>

                                <Route exact path={`${path}/hazu`}>
                                    <OrderHazuSelectContainer />
                                </Route>

                                <Route exact path={`${path}/yajiri`}>
                                    <OrderYajiriSelectContainer />
                                </Route>

                                <Route exact path={`${path}/yashaku`}>
                                    <OrderYashakuSelectContainer />
                                </Route>

                                <Route exact path={`${path}/honsu`}>
                                    <OrderHonsuSelectContainer />
                                </Route>

                                <Route exact path={`${path}/insert`}>
                                    <OrderInsertSelectContainer />
                                </Route>

                                <Route exact path={`${path}/hazumaki`}>
                                    <OrderHazumakiSelectContainer />
                                </Route>

                                <Route exact path={`${path}/rame`}>
                                    <OrderLameSelectContainer />
                                </Route>

                                <Route exact path={`${path}/kokuin`}>
                                    <OrderMozikokuinContainer />
                                </Route>

                                <Route exact path={`${path}/kamon`}>
                                    <OrderKamonContainer />
                                </Route>

                                <Route exact path={`${path}/typezero`}>
                                    <OrderTypeZeroSelectContainer />
                                </Route>

                                <Route exact path={`${path}/deco`}>
                                    <OrderDecorationSelectContainer />
                                </Route>

                                <Route exact path={`${path}/featherinside`}>
                                    <OrderFeatherinsideSelectContainer />
                                </Route>

                                {/* dummy route for mobile 3d confirm view */}
                                <Route
                                    exact
                                    path={`${path}/preview`}
                                    render={(props) => {
                                        if (isMobile) {
                                            return (
                                                <Preview
                                                    {...props}
                                                    isDesktop={isDesktop}
                                                    isMobile={isMobile}
                                                />
                                            );
                                        }
                                        if (isDesktop) {
                                            // FIXME: preview 時にレスポンシブにデスクトップサイズに変更した際の挙動、
                                            // 遷移先がないので、まずは shaft にとばす
                                            return (
                                                <Redirect
                                                    to={{
                                                        pathname: `${url}/shaft`,
                                                    }}
                                                />
                                            );
                                        }
                                        return null;
                                    }}
                                />
                            </Switch>
                        </SlideAnimation>
                    </section>
                </HistoryGoPartsContext.Provider>
            </OrderPathParamsContext.Provider>
            <PortalDialog>
                <div className={loadingClassName}>
                    <Spinner />
                </div>
            </PortalDialog>
        </>
    );
};
